import React, { useState } from 'react';
import ResultSummary from './ResultSummary';
import ResultVariable from './ResultVariable';
import ResultEffects from './ResultEffects';

/**
 * SkillAnalyzerResults — container for all analysis output.
 * Shows: global summary banner + collapsible sections for race/moment variables.
 */

const SectionHeader = ({ title, count, isOpen, onToggle, conflictCount = 0 }) => (
    <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3
            bg-slate-900/50 hover:bg-slate-900 transition-colors rounded-xl border border-slate-700"
    >
        <div className="flex items-center gap-2">
            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400">
                {title}
            </h4>
            {conflictCount > 0 && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full
                    bg-red-900/60 text-red-300 border border-red-700/50">
                    {conflictCount} conflict{conflictCount !== 1 ? 's' : ''}
                </span>
            )}
            <span className="text-xs font-mono text-slate-600">{count}</span>
        </div>
        <svg className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    </button>
);

const SkillAnalyzerResults = ({ analysisResult, skillCount, selectedSkills }) => {
    const [raceOpen, setRaceOpen] = useState(true);
    const [momentOpen, setMomentOpen] = useState(true);

    const hasAnalysis = analysisResult &&
        (analysisResult.raceVariables.length > 0 || analysisResult.momentVariables.length > 0);

    const raceVariables = analysisResult?.raceVariables || [];
    const momentVariables = analysisResult?.momentVariables || [];
    const summary = analysisResult?.summary || null;

    const raceConflicts = raceVariables.filter(v => v.category === 'INCOMPATIBLE_RACE').length;
    const momentConflicts = momentVariables.filter(v => v.category === 'INCOMPATIBLE_MOMENT').length;

    return (
        <div className="space-y-4">
            {/* ── Global summary banner — only with >=2 skills ── */}
            {hasAnalysis && <ResultSummary summary={summary} skillCount={skillCount} />}

            {/* ── Effects table — visible from 1 skill ── */}
            <ResultEffects selectedSkills={selectedSkills} />

            {/* ── Race variables ── */}
            {raceVariables.length > 0 && (
                <div className="space-y-2">
                    <SectionHeader
                        title="Race Variables"
                        count={raceVariables.length}
                        isOpen={raceOpen}
                        onToggle={() => setRaceOpen(o => !o)}
                        conflictCount={raceConflicts}
                    />
                    {raceOpen && (
                        <div className="flex flex-col gap-2 pl-1">
                            {raceVariables.map(result => (
                                <ResultVariable key={result.variable} result={result} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Moment variables ── */}
            {momentVariables.length > 0 && (
                <div className="space-y-2">
                    <SectionHeader
                        title="Moment Variables"
                        count={momentVariables.length}
                        isOpen={momentOpen}
                        onToggle={() => setMomentOpen(o => !o)}
                        conflictCount={momentConflicts}
                    />
                    {momentOpen && (
                        <div className="flex flex-col gap-2 pl-1">
                            {momentVariables.map(result => (
                                <ResultVariable key={result.variable} result={result} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SkillAnalyzerResults;
